import { useState, useEffect, useRef } from "react";
import { useMusic } from "@/contexts/MusicContext";
import { toast } from "@/hooks/use-toast";
import { AppRole } from "@/types/supabase";

interface UseSponsorStreamLimitProps {
  isPlaying: boolean;
  user: any;
  userRole: AppRole | null;
  trackId: string;
}

export function useSponsorStreamLimit({
  isPlaying,
  user,
  userRole,
  trackId,
}: UseSponsorStreamLimitProps) {
  const { setIsPlaying, setCurrentTrack } = useMusic();
  const [sponsorLimitReached, setSponsorLimitReached] = useState(false);
  const [streamDuration, setStreamDuration] = useState(0);
  const playTimer = useRef<NodeJS.Timeout | null>(null);

  // Reset state when track changes
  useEffect(() => {
    console.log("🔄 Sponsor limit reset for new track");
    setSponsorLimitReached(false);
    setStreamDuration(0);
    if (playTimer.current) {
      clearInterval(playTimer.current);
      playTimer.current = null;
    }
  }, [trackId]);

  // Start/stop timer based on playback for sponsors only
  useEffect(() => {
    // Only activate for logged-in sponsors
    if (user && userRole === "sponsor" && isPlaying && !sponsorLimitReached) {
      console.log("⏱️ Starting sponsor preview timer");

      playTimer.current = setInterval(() => {
        setStreamDuration((prev) => {
          const newDuration = prev + 1;

          // Stop at 30 seconds
          if (newDuration >= 30) {
            console.log("🛑 Sponsor 30-second preview limit reached");
            clearInterval(playTimer.current!);
            setSponsorLimitReached(true);
            setIsPlaying(false);

            toast.info("Preview ended", {
              description: "30-second sponsor preview has finished",
              duration: 4000,
            });

            setTimeout(() => {
              setCurrentTrack(null);
            }, 1000);
          }

          return newDuration;
        });
      }, 1000);
    }

    // Cleanup timer when playback stops or conditions change
    return () => {
      if (playTimer.current) {
        clearInterval(playTimer.current);
        playTimer.current = null;
      }
    };
  }, [
    isPlaying,
    user,
    userRole,
    sponsorLimitReached,
    trackId,
    setIsPlaying,
    setCurrentTrack,
  ]);

  return { sponsorLimitReached, streamDuration };
}
